import { injectable, inject } from 'inversify';
import { err, ok } from '@/app/tracking/tracking-api';
import { Scorm2004InteractionDataStore, Scorm2004InteractionDetails } from '@/app/tracking/scorm/2004/store';

export interface Scorm2004QuestionDetails {
  id: string;
  type: string;
  response: string;
  score: number;
  timestamp: Date;
  durationSeconds: number;
  description: string;
}

@injectable()
export class Scorm2004QuestionDataStore {

  constructor(
    @inject(Scorm2004InteractionDataStore)
    private readonly interactionStore: Scorm2004InteractionDataStore) {

  }

  public async get(id: string) {
    const result = await this.interactionStore.get(id);
    if (!result.success) {
      return err(result.error);
    }

    if (result.value === 'not-found') {
      return ok(result.value);
    }

    return ok(this.mapInteractionToQuestion(result.value));
  }

  public async queryAll(id: string) {
    const result = await this.interactionStore.queryAll(id);
    if (!result.success) {
      return err(result.error);
    }

    return ok(result.value.map((x) => this.mapInteractionToQuestion(x)));
  }

  public async update(item: Scorm2004QuestionDetails) {
    return await this.interactionStore.update(this.mapQuestionToInteraction(item));
  }

  public async insert(item: Scorm2004QuestionDetails) {
    return await this.interactionStore.insert(this.mapQuestionToInteraction(item));
  }

  private mapInteractionToQuestion(details: Scorm2004InteractionDetails): Scorm2004QuestionDetails {
    return {
      id: details.id,
      type: details.type,
      response: details.response,
      score: typeof(details.result) === 'number' ? details.result : 0,
      timestamp: details.timestamp,
      durationSeconds: details.durationSeconds,
      description: details.description
    };
  }

  private mapQuestionToInteraction(details: Scorm2004QuestionDetails): Scorm2004InteractionDetails {
    return {
      id: details.id,
      type: details.type,
      response: details.response,
      result: details.score,
      timestamp: details.timestamp,
      durationSeconds: details.durationSeconds,
      description: details.description
    };
  }
}
